import axiosInstance from '../config/axios';

// Generic job polling helper (mirrors validation.service pattern)
const pollAdkJobUntilDone = async (jobId, { intervalMs = 5000, timeoutMs = 15 * 60 * 1000 } = {}) => {
  const start = Date.now();

  while (true) {
    const elapsed = Date.now() - start;
    if (elapsed > timeoutMs) {
      throw new Error('Approval job polling timed out. Please try again later.');
    }

    try {
      const statusResponse = await axiosInstance.get(`/adk/workflow/${jobId}/status`);
      const data = statusResponse.data;

      // Our modified status endpoint returns either { type: 'job', job: {...} } or workflow stub
      if (data?.type === 'job' && data.job) {
        const job = data.job;
        if (job.status === 'completed') {
          return job.result;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Approval job failed');
        }
      }
    } catch (err) {
      console.warn(`ADK job poll error (jobId=${jobId}):`, err.message);
      // keep retrying until timeout
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};

const startAdkJobAndWait = async (path, payload, options = {}) => {
  const { intervalMs, timeoutMs, timeout } = options;

  const startResponse = await axiosInstance.post(path, payload, {
    timeout: timeout || 30000,
  });

  const jobId = startResponse.data?.jobId;
  if (!jobId) {
    throw new Error('Approval job did not return an id');
  }

  return pollAdkJobUntilDone(jobId, { intervalMs, timeoutMs });
};

/**
 * Initiate a modification workflow (Planning Phase)
 * @param {string} sectionId - MongoDB section ID
 * @param {string} sectionNumber - Section number (e.g., "4.2")
 * @param {string} changeRequest - User's change request description
 * @param {string} projectId - Project ID
 * @param {string} studyId - Study ID (optional)
 */
export const initiateModificationWorkflow = async ({
  sectionId,
  sectionNumber,
  changeRequest,
  projectId,
  studyId
}) => {
  try {
    // Set extended timeout for workflow initiation (5 minutes = 300000ms)
    // Planning phase can take time as it queries knowledge graph and generates plan
    const response = await axiosInstance.post(
      '/adk/workflow/modification',
      {
        sectionId,
        sectionNumber,
        changeRequest,
        projectId,
        studyId
      },
      {
        timeout: 300000 // 5 minutes timeout for planning phase
      }
    );

    return response.data;
  } catch (error) {
    console.error('❌ Error initiating modification workflow:', error);
    
    if (error.response) {
      throw new Error(error.response.data.error || 'Failed to initiate modification workflow');
    } else if (error.request) {
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Workflow planning timed out. This operation can take several minutes. Please try again or check server logs.');
      }
      throw new Error('No response from server. The planning phase may still be running. Please check server logs.');
    } else {
      throw new Error(error.message || 'Modification workflow request failed');
    }
  }
};

/**
 * Approve a modification plan and trigger execution
 * @param {string} approvalRequestId - Approval request ID
 * @param {Array} approvedChanges - Array of approved changes
 * @param {string} reviewerId - Reviewer user ID (optional)
 * @param {string} reviewNotes - Review notes (optional)
 */
export const approveModificationPlan = async ({
  approvalRequestId,
  approvedChanges,
  reviewerId = null,
  reviewNotes = null,
  signSessionId,
  meaning = 'approved'
}) => {
  try {
    // Use async job-based approval to avoid HTTP timeouts
    const result = await startAdkJobAndWait(
      '/adk/workflow/approve-async',
      {
        approvalRequestId,
        approvedChanges,
        reviewerId,
        reviewNotes,
        // 21 CFR Part 11 §11.200 electronic signature — verified synchronously
        // server-side before the job is even created (modification.controller.js).
        signSessionId,
        meaning
      },
      {
        intervalMs: 5000,
        timeoutMs: 15 * 60 * 1000 // 15 minutes safety cap for execution
      }
    );

    return result;
  } catch (error) {
    console.error('❌ Error approving modification plan:', error);
    
    if (error.response) {
      throw new Error(error.response.data.error || 'Failed to approve modification plan');
    } else if (error.request) {
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Section approval and execution timed out. This operation can take several minutes. Please try again or check server logs.');
      }
      throw new Error('No response from server. The execution may still be running. Please check server logs.');
    } else {
      throw new Error(error.message || 'Approval request failed');
    }
  }
};

/**
 * Get the status of an approval request
 * @param {string} approvalRequestId - Approval request ID
 */
export const getApprovalRequestStatus = async (approvalRequestId) => {
  try {
    const response = await axiosInstance.get(`/adk/workflow/${approvalRequestId}/status`);

    return response.data;
  } catch (error) {
    console.error('❌ Error fetching approval request status:', error);
    
    if (error.response) {
      throw new Error(error.response.data.error || 'Failed to fetch approval request status');
    } else if (error.request) {
      throw new Error('No response from server. Please check if the server is running.');
    } else {
      throw new Error(error.message || 'Status request failed');
    }
  }
};

export default {
  initiateModificationWorkflow,
  approveModificationPlan,
  getApprovalRequestStatus
};


